// Cells, snapping and footprints (slice 08; design/10-cells-snapping-token-size.md).
//
// A cell is whatever sits between two consecutive lines on each axis; on a
// fitted grid the cells vary. A token's size is in squares and never changes
// on its own. Snapping on: the token sits at the centre of the block of
// cells it occupies, the block anchored on the cell under the hand. A
// footprint of whole squares spans the actual lines of the cells it covers,
// so a 2 × 2 token in a region of uneven cells sits across all four; a free
// size (not a whole number of squares) takes the local cell as its unit.
import { deriveAxis, isGridless, toBoard } from './grid.js';
/** The line positions of an axis in board coordinates, ascending, with the board's own edges as the outer lines. */
export function axisPositions(grid, extent, axis) {
    const along = axis === 'x' ? extent.width : extent.height;
    const a = grid[axis];
    const set = new Set([0, along]);
    for (const l of deriveAxis(a, along))
        set.add(toBoard(a.edge, l.pos, along));
    const sorted = [...set].sort((p, q) => p - q);
    // A line a sliver inside the board's edge — grid detection locks one 2.5 px in on a map with no printed edge line
    // (issue 1290) — is the edge itself: as a square of its own it moved every square's index, and with them the fog
    // and anything else stored by square. Within 15% of the typical square of an edge, it merges into the edge.
    const gaps = sorted.slice(1).map((p, i) => p - sorted[i]).sort((p, q) => p - q);
    const typical = gaps[Math.floor(gaps.length / 2)] ?? 0;
    const sliver = typical * 0.15;
    return sorted.filter((p) => p === 0 || p === along || (p > sliver && p < along - sliver));
}
/** The cell (index between two lines) a coordinate falls in, clamped to the board. */
export function cellIndexAt(positions, coord) {
    if (positions.length < 2)
        return 0;
    let i = 0;
    while (i < positions.length - 2 && coord >= positions[i + 1])
        i += 1;
    return i;
}
/** The span from the start of cell `i` across `count` cells (a fraction interpolates the last), clamped to the board. */
export function cellSpan(positions, i, count) {
    const last = positions.length - 1;
    const start = Math.max(0, Math.min(last, i));
    const whole = Math.floor(count);
    const frac = count - whole;
    const endIndex = Math.min(last, start + whole);
    let to = positions[endIndex];
    if (frac > 0 && endIndex < last)
        to += frac * (positions[endIndex + 1] - positions[endIndex]);
    return { from: positions[start], to };
}
const isWhole = (n) => Number.isInteger(n) && n >= 1;
/**
 * The block of `n` whole cells a token centred at `c` occupies: the block
 * whose span is centred nearest `c`, held inside the board. On a fitted
 * grid the cells are uneven, so the cell under the centre minus half the
 * width is the wrong block whenever the cells around it differ in size —
 * an even-width token was drawn a cell off, and a step drifted sideways.
 */
function blockAt(positions, c, n) {
    const top = positions.length - 1 - n;
    if (top <= 0)
        return 0;
    const probe = Math.max(0, Math.min(top, cellIndexAt(positions, c) - Math.floor(n / 2)));
    let best = probe;
    let bestGap = Infinity;
    for (let i = Math.max(0, probe - n); i <= Math.min(top, probe + n); i += 1) {
        const { from, to } = cellSpan(positions, i, n);
        const gap = Math.abs((from + to) / 2 - c);
        if (gap < bestGap - 1e-9) {
            best = i;
            bestGap = gap;
        }
    }
    return best;
}
/**
 * Where a token of `size` sits when snapping is on and the hand is at `point`:
 * the block of cells anchored so the cell under the hand is the block's
 * middle cell (the left of the middle pair for an even width). Returns the
 * block's centre and its footprint from the actual lines.
 */
export function snapToCells(grid, extent, point, size) {
    // No grid (issue 1035): nothing snaps; the thing stands where it is put.
    if (isGridless(grid))
        return { centre: { x: point.x, y: point.y }, footprint: footprintAt(grid, extent, point, size) };
    const xs = axisPositions(grid, extent, 'x');
    const ys = axisPositions(grid, extent, 'y');
    const w = isWhole(size.w) ? size.w : Math.max(1, Math.round(size.w));
    const h = isWhole(size.h) ? size.h : Math.max(1, Math.round(size.h));
    const ci = Math.max(0, Math.min(xs.length - 1 - w, cellIndexAt(xs, point.x) - Math.floor((w - 1) / 2)));
    const cj = Math.max(0, Math.min(ys.length - 1 - h, cellIndexAt(ys, point.y) - Math.floor((h - 1) / 2)));
    const sx = cellSpan(xs, ci, w);
    const sy = cellSpan(ys, cj, h);
    const footprint = { x: sx.from, y: sy.from, width: sx.to - sx.from, height: sy.to - sy.from };
    return { centre: { x: sx.from + footprint.width / 2, y: sy.from + footprint.height / 2 }, footprint };
}
/**
 * The rectangle a token of `size` centred at `centre` covers. Whole squares
 * take the actual lines of the block around the centre (an even width has
 * its centre on a line); a free size takes the local cell as its unit.
 */
export function footprintAt(grid, extent, centre, size) {
    // No grid (issue 1035): its size in squares of the base pitch, about its centre.
    if (isGridless(grid)) {
        const w = size.w * grid.x.basePitch;
        const h = size.h * grid.y.basePitch;
        return { x: centre.x - w / 2, y: centre.y - h / 2, width: w, height: h };
    }
    const xs = axisPositions(grid, extent, 'x');
    const ys = axisPositions(grid, extent, 'y');
    const along = (positions, c, n) => {
        if (isWhole(n))
            return cellSpan(positions, blockAt(positions, c, n), n);
        const i = cellIndexAt(positions, c);
        const pitch = (positions[i + 1] ?? positions[i] + 1) - positions[i];
        return { from: c - (n * pitch) / 2, to: c + (n * pitch) / 2 };
    };
    const sx = along(xs, centre.x, size.w);
    const sy = along(ys, centre.y, size.h);
    return { x: sx.from, y: sy.from, width: sx.to - sx.from, height: sy.to - sy.from };
}
/** The cell a point is in, as indices — for telling one square from the next along a route. */
export function cellOf(grid, extent, point) {
    return { i: cellIndexAt(axisPositions(grid, extent, 'x'), point.x), j: cellIndexAt(axisPositions(grid, extent, 'y'), point.y) };
}
/** Where a thing stands, in squares as a person counts them (its top-left square, from 1): "column 5, row 3". */
export function squareOf(o, grid, extent) {
    const fp = footprintAt(grid, extent, { x: o.x, y: o.y }, o.size);
    const c = cellOf(grid, extent, { x: fp.x + 1, y: fp.y + 1 });
    return { column: c.i + 1, row: c.j + 1 };
}
/**
 * Where a thing stands in squares, as `squareOf` counts them, but never held on the board (issue 1441): the words a
 * history line says. A point past the board's edge — a move on a larger map than the one at hand, another scene's
 * map, or a map changed since — counts on past the edge at the grid's pitch instead of every point becoming the
 * same edge square. A centre on the board is read exactly as `squareOf` reads it, so a line names the square the
 * board shows.
 */
export function squareAt(o, grid, extent) {
    const held = squareOf(o, grid, extent);
    const along = (a) => {
        const span = a === 'x' ? extent.width : extent.height;
        const n = a === 'x' ? o.size.w : o.size.h;
        const pitch = grid[a].basePitch > 0 ? grid[a].basePitch : 1;
        const start = o[a] - (n * pitch) / 2;
        // A centre on the board is read as the board reads it; only one past its edge counts on.
        if (o[a] >= 0 && o[a] <= span)
            return null;
        const positions = axisPositions(grid, extent, a);
        const last = positions[positions.length - 1];
        const corner = start + 1;
        if (corner < 0)
            return -Math.ceil(-corner / pitch) + 1;
        if (corner >= last)
            return positions.length - 1 + Math.floor((corner - last) / pitch) + 1;
        return cellIndexAt(positions, corner) + 1;
    };
    return { column: along('x') ?? held.column, row: along('y') ?? held.row };
}
export const squareWords = (s) => `column ${s.column}, row ${s.row}`;
/**
 * One square along, for keyboard stepping: the block the token occupies now
 * (its top-left cell read from its centre, as the footprint does), moved by
 * one cell, held inside the board. A 2 × 2 steps sideways without drifting
 * down, which anchoring on a probe cell would have done.
 */
export function stepCell(grid, extent, centre, size, di, dj) {
    // No grid (issue 1035): a square's size along, held on the board.
    if (isGridless(grid)) {
        const hw = (size.w * grid.x.basePitch) / 2;
        const hh = (size.h * grid.y.basePitch) / 2;
        const clamp = (v, lo, hi) => Math.max(lo, Math.min(Math.max(lo, hi), v));
        return { x: clamp(centre.x + di * grid.x.basePitch, hw, extent.width - hw), y: clamp(centre.y + dj * grid.y.basePitch, hh, extent.height - hh) };
    }
    const xs = axisPositions(grid, extent, 'x');
    const ys = axisPositions(grid, extent, 'y');
    const w = isWhole(size.w) ? size.w : Math.max(1, Math.round(size.w));
    const h = isWhole(size.h) ? size.h : Math.max(1, Math.round(size.h));
    const ci = Math.max(0, Math.min(xs.length - 1 - w, blockAt(xs, centre.x, w) + di));
    const cj = Math.max(0, Math.min(ys.length - 1 - h, blockAt(ys, centre.y, h) + dj));
    const sx = cellSpan(xs, ci, w);
    const sy = cellSpan(ys, cj, h);
    return { x: (sx.from + sx.to) / 2, y: (sy.from + sy.to) / 2 };
}
//# sourceMappingURL=cells.js.map